import { useQuery } from "@tanstack/react-query";
import { Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface UserRatingProps {
  userId: string;
  currentUserId?: string;
  showCount?: boolean;
  size?: "sm" | "md" | "lg";
}

export default function UserRating({ userId, currentUserId, showCount = true, size = "md" }: UserRatingProps) {
  // Check if current user has interacted with this user (rented to/from them)
  const { data: hasInteracted } = useQuery({
    queryKey: ["userInteraction", userId, currentUserId],
    enabled: !!currentUserId && userId !== currentUserId,
    queryFn: async () => {
      if (!currentUserId) return false;
      
      const [asRenter, asHost] = await Promise.all([
        supabase
          .from("bookings")
          .select(`
            id,
            listings!inner(owner_id)
          `)
          .eq("status", "confirmed")
          .eq("renter_id", currentUserId),
        supabase
          .from("bookings")
          .select("id")
          .eq("status", "confirmed")
          .eq("renter_id", userId),
      ]);

      if (asRenter.error || asHost.error) return false;

      const renterData = asRenter.data || [];
      const hostData = asHost.data || [];

      const interactedAsRenter = renterData.some((b: any) => b.listings?.owner_id === userId);
      const interactedAsHost = hostData.length > 0; // via RLS, these are bookings where current user is host

      return interactedAsRenter || interactedAsHost;
    },
  });

  // Get user's ratings (only if current user has interacted with them)
  const { data: ratings } = useQuery({
    queryKey: ["userRatings", userId],
    enabled: hasInteracted === true || userId === currentUserId,
    queryFn: async () => {
      // First get user's listings
      const { data: userListings } = await supabase
        .from("listings")
        .select("id")
        .eq("owner_id", userId);

      const listingIds = userListings?.map(l => l.id) || [];

      // Get ratings for user as host (guest_to_host reviews)
      let hostRatings: number[] = [];
      if (listingIds.length > 0) {
        const { data: hostBookingsWithReviews } = await supabase
          .from("bookings")
          .select(`
            id,
            reviews!inner(rating)
          `)
          .eq("status", "confirmed")
          .in("listing_id", listingIds)
          .not("reviews.rating", "is", null);

        hostRatings = hostBookingsWithReviews?.flatMap(b => 
          b.reviews.filter(r => r.rating != null).map(r => r.rating)
        ) || [];
      }

      // Get user's completed bookings as renter
      const { data: userBookings } = await supabase
        .from("bookings")
        .select("id")
        .eq("renter_id", userId)
        .eq("status", "confirmed");

      const bookingIds = userBookings?.map(b => b.id) || [];

      // Get ratings for user as guest (host_to_guest reviews)
      let guestRatings: number[] = [];
      if (bookingIds.length > 0) {
        const { data: guestReviewData } = await supabase
          .from("reviews")
          .select("rating")
          .eq("review_type", "host_to_guest")
          .in("booking_id", bookingIds)
          .not("rating", "is", null);

        guestRatings = guestReviewData?.map(r => r.rating).filter(r => r != null) || [];
      }

      // Combine all ratings
      const allRatings = [...hostRatings, ...guestRatings];

      if (allRatings.length === 0) return null;

      const average = allRatings.reduce((sum, rating) => sum + rating, 0) / allRatings.length;
      return {
        average: Math.round(average * 10) / 10,
        count: allRatings.length
      };
    },
  });

  // Don't show rating if user hasn't interacted (privacy)
  if (currentUserId && userId !== currentUserId && hasInteracted !== true) {
    return null;
  }

  if (!ratings) {
    return null;
  }

  const iconSize = size === "sm" ? "h-3 w-3" : size === "lg" ? "h-5 w-5" : "h-4 w-4";
  const textSize = size === "sm" ? "text-sm" : size === "lg" ? "text-lg" : "text-base";

  return (
    <div className={`flex items-center gap-1 ${textSize}`}>
      <Star className={`${iconSize} fill-warning text-warning`} />
      <span className="font-medium">{ratings.average}</span>
      {showCount && (
        <span className="text-muted-foreground">
          ({ratings.count})
        </span>
      )}
    </div>
  );
}